import { GraphData } from 'force-graph';
import { GalaxyNodeType, GalaxyNode, ORIGIN_NODE } from './galaxy-node';
import { GalaxyLink, GalaxyLinkType } from './galaxy-link';

export type GalaxyParams = {
  spiralArmCount: number;
  spiralTurnCount: number;
  clusterCountPerArm: number;
  clusterRadiusMin: number;
  clusterRadiusMax: number;
  starRadiusMin: number;
  starRadiusMax: number;
  planetRadiusMin: number;
  planetRadiusMax: number;
  ringSpacing: number;
};

export class Galaxy {
  public readonly galaxyData: GraphData<GalaxyNode, GalaxyLink>;
  public readonly params: GalaxyParams;

  private linkMap: Map<GalaxyNode, GalaxyLink[]> = new Map();
  private root: GalaxyNode;

  constructor(graphData: GraphData<GalaxyNode, GalaxyLink>, params: GalaxyParams) {
    if (!graphData || !graphData.nodes || graphData.nodes.length === 0) {
      throw new Error('Invalid graph data');
    }
    this.root = Galaxy.findRoot(graphData);
    this.linkMap = Galaxy.buildLinkMap(graphData);
    this.galaxyData = Galaxy.extractGalaxyData(graphData, this.linkMap, this.root);
    this.params = params;
    this.layoutGalaxyNodes();
  }

  public getChildren(node: GalaxyNode): GalaxyNode[] {
    const childrenLinks = this.linkMap.get(node) || [];
    return childrenLinks.map((link) => link.target);
  }

  public getDescendants(node: GalaxyNode): GalaxyNode[] {
    const descendants: GalaxyNode[] = [];
    const stack: GalaxyNode[] = [node];

    while (stack.length > 0) {
      const current = stack.pop()!;
      descendants.push(current);

      const children = this.getChildren(current);
      stack.push(...children);
    }

    return descendants;
  }

  private static findRoot(graphData: GraphData<GalaxyNode, GalaxyLink>): GalaxyNode {
    const root = graphData.nodes.find((node) => node.type === GalaxyNodeType.SPIRAL) || null;
    if (!root) throw new Error('Root node not found');
    return root;
  }

  private static buildLinkMap(graphData: GraphData<GalaxyNode, GalaxyLink>): Map<GalaxyNode, GalaxyLink[]> {
    const linkMap = new Map<GalaxyNode, GalaxyLink[]>();

    graphData.links.forEach((link) => {
      if (!linkMap.has(link.source)) {
        linkMap.set(link.source, []);
      }
      linkMap.get(link.source)!.push(link);
    });
    return linkMap;
  }

  private static extractGalaxyData(
    graphData: GraphData<GalaxyNode, GalaxyLink>,
    linkMap: Map<GalaxyNode, GalaxyLink[]>,
    root: GalaxyNode,
  ): GraphData<GalaxyNode, GalaxyLink> {
    const galaxyData: GraphData<GalaxyNode, GalaxyLink> = { nodes: [], links: [] };

    const stack: GalaxyNode[] = [root];
    const visitedNodes = new Set<GalaxyNode>();
    while (stack.length > 0) {
      const current = stack.pop()!;

      galaxyData.nodes.push(current);

      const childrenLinks = linkMap.get(current) || [];
      for (const link of childrenLinks) {
        if (!visitedNodes.has(link.target)) {
          visitedNodes.add(link.target);
        } else {
          throw new Error(`Tree structure violation found at node ${link.target.id}`);
        }
        galaxyData.links.push(link);
        stack.push(link.target);
      }
    }

    return galaxyData;
  }

  private layoutGalaxyNodes() {
    const stack = [this.root];

    const originLink = new GalaxyLink(ORIGIN_NODE, this.root, GalaxyLinkType.ORIGIN_TO_SPIRAL);
    this.positionChildNode(originLink, 0, 1);

    while (stack.length > 0) {
      const node = stack.pop()!;

      const childrenLinks = this.linkMap.get(node) || [];
      for (const [index, link] of childrenLinks.entries()) {
        this.positionChildNode(link, index, childrenLinks.length);
        stack.push(link.target);
      }
    }
  }

  private positionChildNode(link: GalaxyLink, index: number, childrenCount: number) {
    const parent = link.source as GalaxyNode;
    const child = link.target as GalaxyNode;
    const linkType = link.type;

    switch (linkType) {
      case GalaxyLinkType.ORIGIN_TO_SPIRAL:
        child.fx = 0;
        child.fy = 0;
        child.fz = 0;
        break;
      case GalaxyLinkType.SPIRAL_TO_CLUSTER:
        this.positionClusterNode(child, parent, index);
        break;
      case GalaxyLinkType.CLUSTER_TO_STAR:
        this.positionStarNode(child, parent, index, childrenCount);
        break;
      case GalaxyLinkType.STAR_TO_PLANET:
        this.positionPlanetNode(child, parent, index, childrenCount);
        break;
      default:
        throw new Error(`Unsupported link type: ${linkType} between ${parent?.id} and ${child.id}`);
    }
  }

  private positionClusterNode(node: GalaxyNode, parent: GalaxyNode, index: number) {
    const armIndex = Math.floor(index / this.params.clusterCountPerArm);
    const indexInArm = index % this.params.clusterCountPerArm;

    // Distribute evenly among arms
    const armPhase = ((2 * Math.PI) / this.params.spiralArmCount) * armIndex;

    // Use smoother normalized position along arm with better bias toward center
    const t = Math.pow((indexInArm + 0.5) / Math.max(1, this.params.clusterCountPerArm), 1.1);

    // Calculate radius using logarithmic spiral formula
    const minRadius = Math.max(0.001, this.params.clusterRadiusMin);
    const maxRadius = Math.max(minRadius + 1, this.params.clusterRadiusMax);

    // Logarithmic spiral calculation
    const totalTheta = this.params.spiralTurnCount * 2 * Math.PI;
    const k = Math.log(maxRadius / minRadius) / totalTheta;
    const theta = armPhase + t * totalTheta;

    // r = r₀ * e^(kθ) - logarithmic spiral formula
    const radius = minRadius * Math.exp(k * (t * totalTheta));

    // Apply position
    node.fx = radius * Math.cos(theta);
    node.fy = radius * Math.sin(theta);

    // Z-coordinate - keep as is since user asked not to add randomness
    node.fz = 0;
  }

  private positionStarNode(node: GalaxyNode, parent: GalaxyNode, index: number, childrenCount: number) {
    const starAngle = Math.PI * 2 * (index / childrenCount);
    const starRadius =
      this.params.starRadiusMin + Math.log(Math.random() + 1) * (this.params.starRadiusMax - this.params.starRadiusMin);
    node.fx = (parent!.fx || 0) + starRadius * Math.cos(starAngle);
    node.fy = (parent!.fy || 0) + starRadius * Math.sin(starAngle);
    node.fz = 0;
  }

  private positionPlanetNode(node: GalaxyNode, parent: GalaxyNode, index: number, childrenCount: number) {
    const planetAngle = Math.PI * 2 * (index / childrenCount);
    const planetRadius = this.params.planetRadiusMin + this.params.ringSpacing * (node.ringIndex + 1);
    node.fx = (parent!.fx || 0) + planetRadius * Math.cos(planetAngle);
    node.fy = (parent!.fy || 0) + planetRadius * Math.sin(planetAngle);
    node.fz = 0;
  }
}
